import React, { useEffect, useRef, useState } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ImageIcon from "@mui/icons-material/Image";
import { IconButton } from "@mui/material";
import { SRLWrapper } from "simple-react-lightbox";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import useDialogState from "@/hooks/useDialogState";
import { FragmentType } from "@/utils/type";

import SimpleLightBox from "./SimpleLightBox";

interface Props {
  className?: string;
  data: FragmentType<typeof mealPhotoFragment>[];
}

const mealPhotoFragment = graphql(`
  fragment MealPhoto on Attachment {
    id
    cloudinaryPreview
  }
`);

const MealPhotos: React.FC<Props> = ({ className, data }) => {
  const { classes, cx } = useStyles();
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainer = useRef<HTMLDivElement>(null);
  const lightbox = useDialogState();
  const isEmpty = data.length === 0;

  useEffect(() => {
    const photo = document.getElementById(`meal_photo_${currentIndex}`);
    if (scrollContainer.current && photo) {
      scrollContainer.current.scrollLeft = photo.offsetLeft;
    }
  }, [currentIndex]);

  const _openLightbox = (index: number) => {
    setCurrentIndex(index);
    lightbox.show();
  };

  const next = () =>
    setCurrentIndex((value) => Math.min(value + 1, data.length - 1));
  const prev = () => setCurrentIndex((value) => Math.max(value - 1, 0));

  return (
    <div className={cx(classes.root, isEmpty && classes.empty, className)}>
      {isEmpty && (
        <div className={classes.emptyContainer}>
          <ImageIcon className={classes.emptyIcon} />
        </div>
      )}

      <SimpleLightBox>
        <div ref={scrollContainer} className={classes.photos}>
          {data.map((photo, index) => (
            <img
              key={photo.id}
              id={`meal_photo_${index}`}
              className={classes.image}
              src={photo.cloudinaryPreview ?? undefined}
              alt=" " // fix borders appearing when src is undefined on firefox (not checked if occurs on other browsers)
            />
          ))}
        </div>
      </SimpleLightBox>

      {data.length > 1 && (
        <div className={classes.controls}>
          <div className={classes.previous}>
            <IconButton
              className={classes.controlButton}
              size="small"
              onClick={() => setCurrentIndex((value) => Math.max(value - 1, 0))}
            >
              <ChevronLeftIcon />
            </IconButton>
          </div>
          <div className={classes.next}>
            <IconButton
              className={classes.controlButton}
              size="small"
              onClick={() =>
                setCurrentIndex((value) => Math.min(value + 1, data.length - 1))
              }
            >
              <ChevronRightIcon />
            </IconButton>
          </div>
          <div className={classes.indicators}>
            {data.map((photo, index) => (
              <div
                key={photo.id}
                className={cx(
                  classes.indicator,
                  index === currentIndex && classes.indicatorActive
                )}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
const useStyles = makeStyles()({
  root: {
    position: "relative",
    width: "100%",
    height: 180,
    backgroundColor: "#D9D9D9",
    scrollSnapType: "x mandatory",
    overflow: "hidden",
  },
  empty: {
    height: 86,
  },
  emptyContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  emptyIcon: {
    color: "#686868",
    fontSize: 40,
  },
  photos: {
    display: "flex",
    width: "100%",
    height: 180,
    scrollBehavior: "smooth",
    scrollSnapType: "x mandatory",
    overflow: "hidden",
  },
  image: {
    flexShrink: 0,
    width: "100%",
    height: 180,
    objectFit: "cover",
    scrollSnapAlign: "start",
  },
  controls: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background: "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 27%, #000 127%)",
  },
  previous: {
    position: "absolute",
    left: 15,
    top: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
  },
  next: {
    position: "absolute",
    right: 15,
    top: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
  },
  controlButton: {
    width: 26,
    height: 26,
    color: "white",
    backgroundColor: "rgba(255,255,255, 0.5)",
    pointerEvents: "auto",
    "&:hover": {
      backgroundColor: "rgba(255,255,255, 0.5)",
    },
  },
  indicators: {
    position: "absolute",
    bottom: 16,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
    gap: 3,
  },
  indicator: {
    width: 6,
    height: 6,
    borderRadius: 9999,
    backgroundColor: "#fff",
    opacity: 0.5,
  },
  indicatorActive: {
    opacity: 1.0,
  },
});

export default MealPhotos;
